import {
  always,
  compose,
  defaultTo,
  either,
  filter,
  fromPairs,
  head,
  isEmpty,
  isNil,
  map,
  omit,
  path,
  pathOr,
  not,
  prop,
  reject,
  toPairs,
  toString,
  when
} from 'ramda';
import { emptyDemographyBlock } from '@@viewHelpers/coverageZone.js';

const safeToString = (value) => (value == null ? undefined : toString(value));

const toInt = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  return parseInt(value, 10);
};

const cleanSocialSecurities = when(
  compose(either(isNil, isEmpty), prop('regime'), defaultTo({}), head),
  always([])
);

// La zone DEFAULT est écrite par le serveur quand la feature est inactive : elle ne doit pas
// remonter dans le select, sinon "Toutes" apparaîtrait comme une zone choisie par l'utilisateur
const DEFAULT_ZONE_CODE = 'DEFAULT';

const zoneLabelOf = (demography) => {
  const salesArea = prop('salesArea', demography);

  if (!salesArea || prop('code', salesArea) === DEFAULT_ZONE_CODE) {
    return '';
  }

  return defaultTo('', prop('label', salesArea));
};

// [{ code, amount }] -> { [code]: 'montant' } : le champ du formulaire est adressé par code
const toFormPayrolls = compose(
  fromPairs,
  map((payroll) => [prop('code', payroll), safeToString(prop('amount', payroll))]),
  defaultTo([]),
  prop('payrolls')
);

// { [code]: 'montant' } -> [{ code, amount }] sans les montants vides
const toPayloadPayrolls = compose(
  map(([code, amount]) => ({ code, amount: toInt(amount) })),
  reject(([, amount]) => either(isNil, isEmpty)(amount)),
  toPairs,
  defaultTo({}),
  prop('payrolls')
);

const toFormBlock = (demography) => ({
  id: prop('id', demography),
  employeeCount: safeToString(prop('employeeCount', demography)),
  employeeAverageAge: safeToString(prop('employeeAverageAge', demography)),
  partnerCount: safeToString(prop('partnerCount', demography)),
  childrenCount: safeToString(prop('childrenCount', demography)),
  payrolls: toFormPayrolls(demography),
  salesArea: { label: zoneLabelOf(demography) },
  socialSecurities: [
    { regime: path(['socialSecurities', 0, 'regime'], demography) }
  ]
});

export const mapDemographiesToFormValues = (getDemography) => {
  const demographies = pathOr([], ['demographies'], getDemography);

  return {
    college: path(['demographies', 0, 'college'], getDemography),
    demographies: isEmpty(demographies)
      ? [emptyDemographyBlock()]
      : map(toFormBlock, demographies)
  };
};

const toPayloadBlock = (college, isMultiZone) => (block) => {
  const zoneLabel = isMultiZone ? path(['salesArea', 'label'], block) : '';

  const payloadBlock = {
    id: prop('id', block),
    college,
    employeeCount: toInt(prop('employeeCount', block)),
    employeeAverageAge: toInt(prop('employeeAverageAge', block)),
    partnerCount: toInt(prop('partnerCount', block)),
    childrenCount: toInt(prop('childrenCount', block)),
    payrolls: toPayloadPayrolls(block),
    socialSecurities: cleanSocialSecurities(
      defaultTo([], prop('socialSecurities', block))
    ),
    salesArea: { label: zoneLabel }
  };

  // Sans zone saisie, on n'envoie rien : le serveur écrit alors la zone DEFAULT
  return isEmpty(defaultTo('', zoneLabel))
    ? omit(['salesArea'], payloadBlock)
    : payloadBlock;
};

export const mapFormValuesToPayload = (values, { isMultiZone }) => {
  const college = prop('college', values);
  const blocks = defaultTo([], prop('demographies', values));
  const keptBlocks = isMultiZone ? blocks : [head(blocks)].filter(Boolean);

  return {
    demographies: map(toPayloadBlock(college, isMultiZone), keptBlocks)
  };
};

export const mapFormValuesToOfferCheck = (values, { isMultiZone }) => ({
  college: prop('college', values),
  zones: isMultiZone
    ? compose(
      filter(compose(not, either(isNil, isEmpty))),
      map(path(['salesArea', 'label'])),
      defaultTo([]),
      prop('demographies')
    )(values)
    : []
});
